import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Person2RoundedIcon from "@mui/icons-material/Person2Rounded";
import { alpha, IconButton, Popover, Stack, Typography } from "@mui/material";
import { FormattedMass } from "components/common/planHistory/FormattedMass";
import type { ChangeComment } from "components/crane/supervisor/useMixChangeComments";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";

dayjs.extend(relativeTime);

interface Props {
  comments: ChangeComment[];
}

export const ChangeCommentHistoryIcon = ({ comments }: Props) => {
  const { t } = useTenantTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton onClick={handleClick} size="small" sx={{ p: 0.25 }}>
        <HistoryRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Stack sx={{ p: 2, maxWidth: 320, maxHeight: 400, overflow: "auto" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("changeHistory")}
          </Typography>
          {comments.map((entry, index) => (
            <Stack
              key={entry.changedAt}
              sx={{
                py: 1,
                borderTop: index > 0 ? "1px solid" : undefined,
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ color: "text.secondary" }}
              >
                <Person2RoundedIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">{entry.changedByName}</Typography>
                <Typography variant="caption" sx={{ fontStyle: "italic" }}>
                  {dayjs(entry.changedAt).fromNow()}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontStyle: "italic",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.info.main, 0.05),
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  borderRadius: 1,
                  padding: 1,
                  wordBreak: "break-word",
                }}
              >
                {entry.comment}
              </Typography>
              {entry.materialDifferences.length > 0 && (
                <Stack sx={{ mt: 0.5 }} spacing={0.25}>
                  {entry.materialDifferences.map((diff) => (
                    <MaterialDiffRow
                      key={`${diff.material_name}-${diff.basket_number}`}
                      diff={diff}
                      t={t}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

const MaterialDiffRow = ({
  diff,
  t,
}: {
  diff: ChangeComment["materialDifferences"][number];
  t: (key: string) => string;
}) => {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <FormattedMass
        value={diff.value_difference}
        unit={diff.unit}
        showSign
        variant="caption"
      />
      <Typography
        variant="caption"
        sx={{
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {diff.material_name}
      </Typography>
      {diff.addition_location === "basket" && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("basket")} {diff.basket_number}
        </Typography>
      )}
    </Stack>
  );
};
